import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, refresh as apiRefresh, logout as apiLogout, getProfile } from '../../api/auth';
import { setAccessToken, clearAccessToken, getAccessToken } from '../../api/http';
import { clearHistoryAfterLogout, redirectToDashboard } from '../../utils/historyManager';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessTokenState] = useState(null);
  const [refreshToken, setRefreshTokenState] = useState(() => localStorage.getItem('refreshToken'));
  const [loading, setLoading] = useState(true);

  // Internal helper to apply tokens
  const applyTokens = useCallback(({ access, refresh }) => {
    if (access) {
      setAccessToken(access);
      setAccessTokenState(access);
    }
    if (refresh) {
      localStorage.setItem('refreshToken', refresh);
      setRefreshTokenState(refresh);
    }
  }, []);

  const clearTokens = useCallback(() => {
    clearAccessToken();
    setAccessTokenState(null);
    localStorage.removeItem('refreshToken');
    setRefreshTokenState(null);
  }, []);

  // Bootstrap: attempt refresh if refresh token exists
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setLoading(true);
      const rt = localStorage.getItem('refreshToken');
      if (rt) {
        try {
          const { data } = await apiRefresh({ refreshToken: rt });
          // backend may nest tokens under tokens or flat structure
          const tokens = data.tokens || data;
          applyTokens({ access: tokens.accessToken, refresh: tokens.refreshToken });
          // attempt to get profile for user details
          try {
            const profile = await getProfile();
            if (!cancelled) {
              setUser(profile.data.user || null);
              // Clear any auth-related browser history when user is restored from token
              if (profile.data.user && typeof window !== 'undefined') {
                const authRoutes = ['/signin', '/signup', '/forgot', '/verify-otp', '/reset-password'];
                if (authRoutes.includes(window.location.pathname)) {
                  const dashboardPath = redirectToDashboard(profile.data.user.role);
                  window.location.href = dashboardPath;
                }
              }
            }
          } catch {/* ignore profile failure */}
        } catch {
          clearTokens();
          if (!cancelled) setUser(null);
        }
      }
      if (!cancelled) setLoading(false);
    }
    bootstrap();
    return () => { cancelled = true; };
  }, [applyTokens, clearTokens]);

  // Login
  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    try {
      const { data } = await apiLogin({ email, password });
      
      // Backend returns { success: true, user: {...}, tokens: {...} }
      if (!data.success) {
        throw new Error(data.message || 'Login failed');
      }
      
      const tokens = data.tokens;
      if (!tokens?.accessToken || !tokens?.refreshToken) {
        throw new Error('Invalid login response - missing tokens');
      }
      
      applyTokens({ access: tokens.accessToken, refresh: tokens.refreshToken });
      if (data.user) setUser(data.user);
      
      // Return the structure expected by SignIn.jsx
      return {
        success: true,
        user: data.user,
        tokens: data.tokens
      };
    } catch (e) {
      clearTokens();
      setUser(null);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [applyTokens, clearTokens]);

  // Logout
  const logout = useCallback(async () => {
    const rt = refreshToken || localStorage.getItem('refreshToken');
    try {
      if (rt) {
        await apiLogout({ refreshToken: rt });
      }
    } catch {/* ignore logout errors */}
    clearTokens();
    setUser(null);
    
    // Clear browser history and force redirect
    clearHistoryAfterLogout();
  }, [refreshToken, clearTokens]);

  const value = {
    user,
    accessToken,
    refreshToken,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    // expose current access token via getter for any advanced use
    getAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    // If context is null, it means we are outside the provider
    // OR we have multiple context instances.
    console.warn('AuthContext is null. Make sure you are wrapping your app in AuthProvider and imports match.');
    // Check if we are in a text environment where throwing might be too aggressive? 
    // Usually throwing is better.
    // throw new Error('useAuthContext must be used within an AuthProvider');
    
    // For now, let's return a safe mock to prevent "destructure" crash and see logs
    return {
       user: null,
       accessToken: null,
       refreshToken: null,
       loading: true, // effectively stuck in loading
       isAuthenticated: false,
       login: async () => {},
       logout: async () => {},
       getAccessToken: () => null
    };
  }
  return context;
}

// Alias for consistency with other parts of the app
export const useAuth = useAuthContext;
